import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Loader2,
  Wrench,
  FileText,
  Cpu,
  ChevronDown,
  UserCheck,
  AlertTriangle,
  Info,
  X,
  Clock,
  Layers,
} from "lucide-react";
import { maintenanceAPI, machinesAPI, sopsAPI, errorAPI } from "../api";
import toast from "react-hot-toast";
import clsx from "clsx";
import { getIstDateInputValue } from "../utils/date";

const ScheduleMaintenance = ({
  isOpen = true,
  onClose,
  initialMachineId,
  initialBugId,
  initialDescription,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const prefilledMachineId =
    initialMachineId ||
    location.state?.machineId?._id ||
    location.state?.machineId ||
    "";
  const prefilledBugId = initialBugId || location.state?.bugId || null;
  const prefilledDesc =
    initialDescription || location.state?.description || "";

  const [formData, setFormData] = useState({
    machineId: prefilledMachineId,
    sopId: "",
    technicianId: "",
    title: prefilledBugId
      ? `Resolve Error: ${prefilledDesc || prefilledBugId.slice(-6)}`
      : "",
    description: prefilledDesc,
    scheduledDate: getIstDateInputValue(),
    maintenanceType: prefilledBugId ? "corrective" : "preventive",
    bugId: prefilledBugId,
  });

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate("/maintenance");
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Sync state if incoming props or router state change
  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        machineId: prefilledMachineId || prev.machineId,
        bugId: prefilledBugId ?? prev.bugId,
        title:
          prev.title ||
          (prefilledBugId
            ? `Resolve Error: ${prefilledDesc || prefilledBugId.slice(-6)}`
            : ""),
        description: prev.description || prefilledDesc,
        maintenanceType: prefilledBugId ? "corrective" : prev.maintenanceType,
      }));
    }
  }, [isOpen, prefilledMachineId, prefilledBugId, prefilledDesc]);

  // Queries
  const { data: machines, isLoading: machinesLoading } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () => machinesAPI.getAll({ limit: 100 }).then((res) => res.data.data),
    enabled: isOpen,
  });

  const { data: technicians, isLoading: techsLoading } = useQuery({
    queryKey: ["technicians-list"],
    queryFn: () => maintenanceAPI.getTechnicians().then((res) => res.data.data),
    enabled: isOpen,
  });

  const { data: sops, isLoading: sopsLoading } = useQuery({
    queryKey: ["sops-list", formData.machineId],
    queryFn: () =>
      sopsAPI
        .getAll({ machineId: formData.machineId, status: "approved" })
        .then((res) => res.data.data),
    enabled: isOpen && !!formData.machineId,
  });

  const { data: openBugs, isLoading: bugsLoading } = useQuery({
    queryKey: ["open-bugs", formData.machineId],
    queryFn: () =>
      errorAPI
        .getAll({ machineId: formData.machineId, status: "Open" })
        .then((res) => res.data.data),
    enabled: isOpen && !!formData.machineId,
  });

  // Mutation
  const scheduleMutation = useMutation({
    mutationFn: (data) => maintenanceAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance"] });
      toast.success("Maintenance scheduled successfully!");
      handleClose();
    },
    onError: (err) =>
      toast.error(
        "Failed to schedule: " + (err.response?.data?.error || err.message)
      ),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Task title is required");
      return;
    }
    if (!formData.machineId) {
      toast.error("Please select a target machine");
      return;
    }
    const submissionData = { ...formData, bugId: formData.bugId || null };
    scheduleMutation.mutate(submissionData);
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col my-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100/80 flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                  Schedule Maintenance Task
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Create a work order for preventive or corrective servicing
                </p>
              </div>
            </div>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="p-5 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(85vh-130px)]">
              {/* Row 1: Target Machine & Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Target Machine <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Cpu className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <select
                      required
                      value={formData.machineId}
                      disabled={machinesLoading}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          machineId: e.target.value,
                          sopId: "",
                          bugId: null,
                        })
                      }
                      className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {machinesLoading ? "Loading machines..." : "Select machine..."}
                      </option>
                      {machines?.map((m) => (
                        <option key={m.id || m._id} value={m.id || m._id}>
                          {m.name} {m.model ? `(${m.model})` : ""}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Maintenance Type <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Layers className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <select
                      value={formData.maintenanceType}
                      onChange={(e) =>
                        setFormData({ ...formData, maintenanceType: e.target.value })
                      }
                      className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs cursor-pointer"
                    >
                      <option value="preventive">Preventive Maintenance</option>
                      <option value="corrective">Corrective Repair</option>
                      <option value="predictive">Predictive Servicing</option>
                    </select>
                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Row 2: Title */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Task Title <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Wrench className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="e.g., Annual Spindle Bearing Lubrication"
                    className="w-full pl-10 pr-3.5 py-2.5 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-xs"
                  />
                </div>
              </div>

              {/* Row 3: Linked SOP & Linked Error Log */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Standard Operating Procedure
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <select
                      value={formData.sopId}
                      disabled={!formData.machineId || sopsLoading}
                      onChange={(e) =>
                        setFormData({ ...formData, sopId: e.target.value })
                      }
                      className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {!formData.machineId
                          ? "Select machine first"
                          : sopsLoading
                            ? "Loading SOPs..."
                            : "No SOP attached (Optional)"}
                      </option>
                      {sops?.map((sop) => (
                        <option key={sop.id || sop._id} value={sop.id || sop._id}>
                          {sop.title}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Linked Error Log
                  </label>
                  <div className="relative">
                    <AlertTriangle className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <select
                      value={formData.bugId || ""}
                      disabled={!formData.machineId || bugsLoading}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bugId: e.target.value || null,
                          maintenanceType: e.target.value ? "corrective" : formData.maintenanceType,
                        })
                      }
                      className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {!formData.machineId
                          ? "Select machine first"
                          : bugsLoading
                            ? "Loading open errors..."
                            : "None (Routine Task)"}
                      </option>
                      {openBugs?.map((b) => (
                        <option key={b.id || b._id} value={b.id || b._id}>
                          {b.description || `Error #${(b.id || b._id).slice(-6)}`}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Row 4: Technician & Scheduled Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Assign Technician
                  </label>
                  <div className="relative">
                    <UserCheck className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <select
                      value={formData.technicianId}
                      disabled={techsLoading}
                      onChange={(e) =>
                        setFormData({ ...formData, technicianId: e.target.value })
                      }
                      className="w-full appearance-none pl-10 pr-10 py-2.5 bg-white hover:bg-slate-50/50 border border-slate-200 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {techsLoading ? "Loading technicians..." : "Unassigned (Open pool)"}
                      </option>
                      {technicians?.map((tech) => (
                        <option key={tech.id || tech._id} value={tech.id || tech._id}>
                          {tech.firstName} {tech.lastName}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Scheduled Date <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="date"
                      required
                      min={getIstDateInputValue()}
                      value={formData.scheduledDate}
                      onChange={(e) =>
                        setFormData({ ...formData, scheduledDate: e.target.value })
                      }
                      className="w-full pl-10 pr-3.5 py-2.5 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Row 5: Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Detailed Instructions (Optional)
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Provide specific notes, target measurements, or special precautions for the technician..."
                  className="w-full p-3 bg-white hover:bg-slate-50/50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-xs resize-none"
                />
              </div>

              {/* Info Banner */}
              <div className="p-3.5 bg-blue-50/70 border border-blue-200/80 rounded-xl flex items-start gap-3">
                <div className="p-1 rounded-lg bg-blue-100 text-blue-700 shrink-0 mt-0.5">
                  <Info className="w-4 h-4" />
                </div>
                <p className="text-xs text-blue-800 leading-relaxed font-medium">
                  Scheduling this operation creates a trackable maintenance log ticket. If a technician is selected, they will receive an immediate assignment notification.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 sm:px-4 hover:border-slate-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={scheduleMutation.isPending}
                className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 whitespace-nowrap inline-flex items-center gap-1.5 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {scheduleMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-1" />
                    Schedule Task
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default ScheduleMaintenance;
